import { useEffect, useRef } from "react";

import type { CheckoutSession } from "@/types";
import { CheckoutStatus } from "@/types";
import type { JerkCardsResponse } from "@/services/cosmos/schema/jerk";
import { browserAgent } from "@/shared/telemetry/newrelic";
import {
  emitPaymentMethodsShown,
  emitApplePayTracking,
} from "@/shared/tracking/analytics";

function resolveDefaultMethodType(
  applePayAvailable: boolean,
  savedCardsCount: number,
): "apple_pay" | "card_on_file" | "no_card_on_file" {
  if (applePayAvailable) return "apple_pay";
  if (savedCardsCount > 0) return "card_on_file";
  return "no_card_on_file";
}

export function usePaymentMethodsTracking(
  session?: CheckoutSession,
  savedCards?: JerkCardsResponse,
): void {
  const hasEmittedPaymentMethodsShownRef = useRef(false);

  useEffect(() => {
    if (
      !session ||
      hasEmittedPaymentMethodsShownRef.current ||
      session.status !== CheckoutStatus.AWAIT_PAYMENT_METHOD_SELECTION
    ) {
      return;
    }

    if (savedCards !== undefined) {
      const deviceSupportsApplePay =
        "ApplePaySession" in window &&
        typeof (window as { ApplePaySession?: unknown }).ApplePaySession !==
          "undefined";
      const applePayAvailable =
        session.country.applePayAllowed &&
        deviceSupportsApplePay &&
        (savedCards?.metaData?.applePayPreference ?? true);

      emitPaymentMethodsShown(
        session,
        savedCards,
        applePayAvailable,
        deviceSupportsApplePay,
      );

      const savedCardsCount = savedCards?.cards?.length ?? 0;
      const defaultMethodType = resolveDefaultMethodType(
        applePayAvailable,
        savedCardsCount,
      );

      browserAgent.addPageAction("PAYMENT_METHOD_DEFAULT_SHOWN", {
        default_method_type: defaultMethodType,
        apple_pay_available: applePayAvailable,
        device_supports_apple_pay: deviceSupportsApplePay,
        saved_cards_count: savedCardsCount,
      });

      if (applePayAvailable) {
        emitApplePayTracking("apple_pay_available", session);
        emitApplePayTracking("apple_pay_button_shown", session);
      }

      hasEmittedPaymentMethodsShownRef.current = true;
    }
  }, [session, savedCards]);
}
